import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface QuickActions {
  camera: (() => void) | null;
  upload: (() => void) | null;
  jobTag: (() => void) | null;
}

// Demo-only: lets the sidebar simulate what a Wayne or Cary team member would
// see, without swapping the logged-in account. Purely presentational — the
// backend still enforces real access. Persisted to sessionStorage so a
// refresh keeps the chosen view (mirrors the DemoGuide pattern).
export type DemoTeam = 'wayne' | 'cary' | null;
const DEMO_TEAM_KEY = 'demo-team-view';

// Which material-type tab is active on Material Intake — the Sidebar reads
// this to show the right set of Material Intake children (Follow Ups/Overs
// belong to Proxy/Corp Action only; Regulatory has its own, smaller set).
export type MaterialIntakeTab = 'proxy' | 'regulatory';

// Which top-level app section the user is currently working in — driven off
// the route by AppShell (see detectSection there) every time it changes to a
// page that clearly belongs to one section. /ace itself carries no section
// info in its URL, so this is "sticky": it holds the last real section the
// user visited, letting Ace's landing screen match context without forking
// into a second page. Defaults to 'fulfillment' — today's existing behavior
// for a user who hasn't visited Proxy yet.
export type ActiveSection = 'proxy' | 'fulfillment';

interface AppShellContextValue {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  topbarTitle: string;
  topbarSub: string;
  topbarActions: ReactNode | null;
  setTopbar: (title: string, sub?: string) => void;
  setTopbarActions: (actions: ReactNode | null) => void;
  quickActions: QuickActions;
  setQuickActions: (actions: QuickActions) => void;
  demoTeam: DemoTeam;
  setDemoTeam: (team: DemoTeam) => void;
  materialIntakeTab: MaterialIntakeTab;
  setMaterialIntakeTab: (tab: MaterialIntakeTab) => void;
  activeSection: ActiveSection;
  setActiveSection: (section: ActiveSection) => void;
  activeDemoIdea: number;
  setActiveDemoIdea: (idea: number) => void;
}

const AppShellContext = createContext<AppShellContextValue | null>(null);

const EMPTY_QUICK_ACTIONS: QuickActions = { camera: null, upload: null, jobTag: null };

export function AppShellProvider({ children }: { children: ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [topbarTitle, setTopbarTitle] = useState("Ace");
  const [topbarSub, setTopbarSub] = useState("");
  const [topbarActions, setTopbarActions] = useState<ReactNode | null>(null);
  const [quickActions, setQuickActions] = useState<QuickActions>(EMPTY_QUICK_ACTIONS);
  const [demoTeam, setDemoTeamState] = useState<DemoTeam>(() => {
    const saved = sessionStorage.getItem(DEMO_TEAM_KEY);
    return saved === 'wayne' || saved === 'cary' ? saved : null;
  });
  const [materialIntakeTab, setMaterialIntakeTab] = useState<MaterialIntakeTab>('proxy');
  const [activeSection, setActiveSection] = useState<ActiveSection>('fulfillment');
  const [activeDemoIdea, setActiveDemoIdea] = useState<number>(0);

  const toggleSidebar = useCallback(() => setIsCollapsed((v) => !v), []);

  const setTopbar = useCallback((title: string, sub = "") => {
    setTopbarTitle(title);
    setTopbarSub(sub);
  }, []);

  const setDemoTeam = useCallback((team: DemoTeam) => {
    setDemoTeamState(team);
    if (team) sessionStorage.setItem(DEMO_TEAM_KEY, team);
    else sessionStorage.removeItem(DEMO_TEAM_KEY);
  }, []);

  return (
    <AppShellContext.Provider
      value={{
        isCollapsed,
        toggleSidebar,
        topbarTitle,
        topbarSub,
        topbarActions,
        setTopbar,
        setTopbarActions,
        quickActions,
        setQuickActions,
        demoTeam,
        setDemoTeam,
        materialIntakeTab,
        setMaterialIntakeTab,
        activeSection,
        setActiveSection,
        activeDemoIdea,
        setActiveDemoIdea,
      }}
    >
      {children}
    </AppShellContext.Provider>
  );
}

export function useAppShell(): AppShellContextValue {
  const ctx = useContext(AppShellContext);
  if (!ctx) throw new Error("useAppShell must be used inside AppShellProvider");
  return ctx;
}
